'use client';

import { useState } from 'react';
import { Check, Circle, X } from 'lucide-react';
import {
  changedPasswordRules,
  evaluatePasswordRules,
  passwordRuleSignature,
  type PasswordPolicy,
} from '@icebreaker/shared/domain';
import { useTranslation } from './i18n-context';
import styles from './password-rule-list.module.css';

export interface PasswordRuleListProps {
  /** Şifre alanının `aria-describedby`ı bu kimliği gösterir. */
  readonly id: string;
  readonly password: string;
  /** Ürün (`PRODUCT_PASSWORD_POLICY`) ya da personel (`STAFF_PASSWORD_POLICY`). */
  readonly policy: PasswordPolicy;
  /** Gönderim denendi ve reddedildi: sağlanmayan maddeler vurgulanır. */
  readonly attempted?: boolean;
}

/**
 * ŞİFRE KURALLARI — CANLI KONTROL LİSTESİ (elle test B-29, 09.10.2026).
 *
 * Maddeler POLİTİKADAN üretilir (`evaluatePasswordRules`): sunucunun Joi
 * şeması aynı listeyi koşar, ekranda ayrı bir kural metni yazılmaz.
 *
 * ERİŞİLEBİLİRLİK (README 4.9): durum renkle değil İKON BİÇİMİ + METİNLE
 * bildirilir (sağlandı: onay işareti, bekliyor: boş daire, gönderimde eksik:
 * çarpı) ve her maddenin sonunda ekran okuyucu için "sağlandı/eksik" sözcüğü
 * vardır. Liste canlı bölge DEĞİLDİR (her tuşta tüm listeyi okurdu); ayrı,
 * görünmez bir `role="status"` bölgesi yalnız bir maddenin durumu DEĞİŞTİĞİNDE
 * o maddeyi duyurur. Metin aynı kaldıkça DOM değişmez, okuyucu susar.
 */
export function PasswordRuleList({
  id,
  password,
  policy,
  attempted = false,
}: PasswordRuleListProps) {
  const t = useTranslation();
  const statuses = evaluatePasswordRules(password, policy);
  const signature = passwordRuleSignature(statuses);
  const [previous, setPrevious] = useState(signature);
  const [announcement, setAnnouncement] = useState('');

  // Türetilmiş durum (React'in "önceki değeri sakla" kalıbı): efekt yok, tek geçiş.
  if (signature !== previous) {
    const changed = changedPasswordRules(previous, statuses);
    setPrevious(signature);
    setAnnouncement(
      changed
        .map((status) =>
          t.t('auth.password.ruleAnnouncement', {
            rule: t.t(status.labelKey, status.params),
            state: t.t(status.met ? 'auth.password.ruleMet' : 'auth.password.ruleUnmet'),
          }),
        )
        .join('. '),
    );
  }

  return (
    <div className={styles.rules}>
      <ul id={id} className={styles.list} aria-label={t.t('auth.password.ruleListLabel')}>
        {statuses
          .filter((status) => status.listed)
          .map((status) => {
            const missing = attempted || status.violated;
            const tone = status.met ? styles.met : missing ? styles.missing : styles.pending;
            // Ekran okuyucu maddeyi "kural, sağlandı/eksik" diye okur (mobil etiketle aynı).
            const state = `, ${t.t(status.met ? 'auth.password.ruleMet' : 'auth.password.ruleUnmet')}`;
            return (
              <li
                key={status.id}
                className={`${styles.item} ${tone}`}
                data-rule={status.id}
                data-met={status.met ? 'true' : 'false'}
              >
                {status.met ? (
                  <Check size={14} strokeWidth={2.5} aria-hidden="true" className={styles.icon} />
                ) : missing ? (
                  <X size={14} strokeWidth={2.5} aria-hidden="true" className={styles.icon} />
                ) : (
                  <Circle size={14} aria-hidden="true" className={styles.icon} />
                )}
                <span className={styles.label}>{t.t(status.labelKey, status.params)}</span>
                <span className={styles.srOnly}>{state}</span>
              </li>
            );
          })}
      </ul>
      <p className={styles.srOnly} role="status" aria-live="polite" data-rule-announcement="">
        {announcement}
      </p>
    </div>
  );
}
